import { Container, Stack, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';
import { makeStyles } from 'tss-react/mui';
import PaiListItem from '../features/ui/base-components/pai-list-item';
import PaiButton from '../features/ui/base-components/pai-button';
import PaiTextArea from '../features/ui/base-components/pai-text-area';
import Logger from '../logger';
import { Shadows } from '../theme';

const OauthSuccess: React.FC = () => {
  const { classes } = useStyles();
  const { t } = useTranslation();

  const [searchParams] = useSearchParams();
  const code = searchParams.get('code') || '';
  const [copied, setCopied] = useState(false);

  const copyTextToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
    } catch (e) {
      // fallback for outlook in IFrame
      // for more information see here: https://web.dev/async-clipboard/#permissions-policy-integration
      Logger.error(e);
    }
  };

  useEffect(() => {
    if (copied) {
      setTimeout(() => {
        setCopied(false);
      }, 2500);
    }
  }, [copied]);

  return (
    <Container className={classes.container} style={{ maxWidth: '1920px' }}>
      <div className={classes.paper}>
        <div className={classes.titleContainer}>
          <Typography className={classes.title}>SAIA</Typography>
          <Typography className={classes.title}>{t('authMsAccountTitle')}</Typography>
        </div>
        <div className={classes.content}>
          <Stack gap={3}>
            <Stack gap={2}>
              <Typography className={classes.subTitle}>{t('authMsAccountContent1')}</Typography>
              <Stack gap={0.5} sx={{ paddingLeft: '12px', paddingRight: '12px' }}>
                <PaiListItem text={t('authMsAccountContent2')} />
                <PaiListItem text={t('authMsAccountContent3')} />
                <PaiListItem text={t('authMsAccountContent4')} />
              </Stack>
            </Stack>
            <Stack gap={2}>
              <PaiTextArea value={code} readOnly={true} variant={'background'} selectOnFocus={true} rows={3} />
              <PaiButton text={!copied ? t('copy') : t('copied')} size={'medium'} border={'rounded'} action={copyTextToClipboard} />
            </Stack>
          </Stack>
        </div>
      </div>
    </Container>
  );
};

const useStyles = makeStyles()((theme) => ({
  container: {
    height: '100vh',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    [theme.breakpoints.down('sm')]: {
      padding: '0',
      margin: '0',
      height: '100%',
    },
  },
  paper: {
    boxShadow: Shadows.GREY_SHADOW_MAIN,
    width: '100%',
    maxWidth: '450px',
    borderRadius: '12px',
    overflow: 'hidden',
    [theme.breakpoints.down('sm')]: {
      maxWidth: 'unset',
      boxShadow: 'unset',
      borderRadius: '0',
    },
  },
  titleContainer: {
    width: '100%',
    padding: '24px',
    background: 'linear-gradient(to right, #5044FF, #4AA4FF)',
    position: 'relative',
    '&::after': {
      content: '""',
      position: 'absolute',
      top: '50%',
      right: '12px',
      transform: 'translateY(-50%)',
      width: '70px',
      height: '70px',
      backgroundImage: 'url(/dashboard/images/logos/product_logo_white.svg)',
      backgroundSize: 'contain',
      backgroundRepeat: 'no-repeat',
      backgroundPosition: 'center',
    },
  },
  title: {
    color: '#FFF',
    fontSize: theme.typography.pxToRem(20),
    fontWeight: 'bold',
  },
  content: {
    padding: '20px 16px 16px 16px',
  },
  subTitle: {
    fontSize: theme.typography.pxToRem(16),
  },
}));

export default OauthSuccess;
